import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionHeading, Reveal } from './_shared';
import { fetchBrokerMap } from '../../../services/brokerMapService';
import type { BrokerMapEntry } from '../../../types/brokerMap';
import BrokerMapView, { type CityCluster } from '../../brokers-map/components/BrokerMapView';
import { formatAmount } from '../../../utils/numberFormat';

const MAP_ENABLED = import.meta.env.VITE_BROKER_MAP_ENABLED === 'true';

const TYPE_META: Record<BrokerMapEntry['type'], { chip: string; color: string; en: string; ar: string }> = {
  individual: { chip: 'bg-blue-50 text-blue-700', color: '#03759F', en: 'Individual', ar: 'فرد' },
  company: { chip: 'bg-purple-50 text-purple-700', color: '#003930', en: 'Company', ar: 'شركة' },
  developer: { chip: 'bg-amber-50 text-amber-700', color: '#B58A0A', en: 'Developer', ar: 'مطوّر' },
};

function brokerHref(b: BrokerMapEntry): string {
  if (b.type === 'developer') return `/developers/${b.slug}`;
  if (b.type === 'company') return `/companies/${b.slug}`;
  return `/broker/${b.slug}`;
}

// Merged from the current site's /brokers/map: a "Trusted brokers" homepage band
// — top verified brokers as cards + a lazy, compact map preview that opens the
// full interactive map. Gated on VITE_BROKER_MAP_ENABLED (cards-only when off).
export default function TrustedBrokers() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();

  const [brokers, setBrokers] = useState<BrokerMapEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const mapWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBrokerMap({ verified: true })
      .then((list) => {
        if (!cancelled) setBrokers(list);
      })
      .catch(() => {
        if (!cancelled) setBrokers([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only mount Google Maps once the preview scrolls near the viewport — keeps it
  // off the critical path for visitors who never reach this band.
  useEffect(() => {
    if (!MAP_ENABLED) return;
    const el = mapWrapRef.current;
    if (!el || showMap) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowMap(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [showMap, loaded]);

  const topBrokers = useMemo(
    () => [...brokers].sort((a, b) => b.totals.score - a.totals.score).slice(0, 6),
    [brokers],
  );

  const clusters = useMemo<CityCluster[]>(() => {
    const map = new Map<string, CityCluster>();
    for (const b of brokers) {
      for (const c of b.cities) {
        if (c.lat == null || c.lng == null) continue;
        const key = c.cityId || `${c.lat},${c.lng}`;
        let g = map.get(key);
        if (!g) {
          g = { cityId: key, name: c.name, lat: c.lat, lng: c.lng, count: 0, totalListings: 0 };
          map.set(key, g);
        }
        g.count += 1;
        g.totalListings += c.listingCount;
      }
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [brokers]);

  // Nothing verified to show yet → skip the band entirely (never an empty shell).
  if (loaded && topBrokers.length === 0) return null;

  return (
    <section className="border-t border-[var(--pv-border-subtle)] bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          title={t('home.v2.brokers.title')}
          action={
            MAP_ENABLED
              ? { label: t('home.v2.brokers.viewMap'), to: '/brokers/map' }
              : { label: t('home.v2.brokers.viewAll'), to: '/trusted-brokers' }
          }
          isRTL={isRTL}
        />

        <div className={`grid gap-6 ${MAP_ENABLED ? 'lg:grid-cols-2' : ''}`}>
          {/* Top brokers */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(loaded ? topBrokers : Array.from({ length: 4 })).map((b, i) => {
              if (!b) {
                return <div key={i} className="h-[76px] animate-pulse rounded-2xl border border-[var(--pv-border-soft)] bg-white" />;
              }
              const broker = b as BrokerMapEntry;
              const meta = TYPE_META[broker.type];
              return (
                <Reveal key={broker.id} delay={i * 0.05} className="h-full">
                  <Link
                    to={brokerHref(broker)}
                    className="group flex h-full items-center gap-3 rounded-2xl border border-[var(--pv-border-soft)] bg-white p-3 transition hover:-translate-y-0.5 hover:border-mq-primary/40 hover:shadow-[0_16px_36px_-24px_rgba(0,77,64,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mq-primary/40"
                  >
                    {broker.avatarUrl ? (
                      <img
                        src={broker.avatarUrl}
                        alt=""
                        width={44}
                        height={44}
                        loading="lazy"
                        className="h-11 w-11 shrink-0 rounded-xl object-cover ring-1 ring-[#DDEEEB]"
                      />
                    ) : (
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white"
                        style={{ background: meta.color }}
                        aria-hidden="true"
                      >
                        {broker.displayName.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span className="truncate font-semibold text-ink">{broker.displayName}</span>
                        {broker.verified && (
                          <i className="ri-verified-badge-fill text-[15px] text-mq-primary" aria-hidden="true" />
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
                        <span className={`rounded-full px-1.5 py-0.5 font-medium ${meta.chip}`}>
                          {isRTL ? meta.ar : meta.en}
                        </span>
                        <span className="inline-flex items-center gap-0.5 tabular-nums">
                          <i className="ri-home-4-line" aria-hidden="true" />
                          {t('home.v2.brokers.listings', { n: formatAmount(broker.totals.listings) })}
                        </span>
                        {broker.avgRating > 0 && (
                          <span className="inline-flex items-center gap-0.5 tabular-nums">
                            <i className="ri-star-fill text-[#B58A0A]" aria-hidden="true" />
                            {broker.avgRating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                    <i
                      className={`text-lg text-[#B7DDD7] transition-colors group-hover:text-mq-primary ${
                        isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'
                      }`}
                      aria-hidden="true"
                    />
                  </Link>
                </Reveal>
              );
            })}
          </div>

          {/* Compact map preview → full interactive map */}
          {MAP_ENABLED && (
            <div
              ref={mapWrapRef}
              className="relative h-72 overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-purple-50 lg:h-auto lg:min-h-[20rem]"
            >
              {showMap && clusters.length > 0 ? (
                <BrokerMapView
                  clusters={clusters}
                  color="#055A4C"
                  selectedCity={null}
                  onSelectCity={() => navigate('/brokers/map')}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-mq-primary-light">
                  <i className="ri-map-2-line text-4xl" aria-hidden="true" />
                </div>
              )}
              <Link
                to="/brokers/map"
                className={`absolute bottom-3 z-[500] inline-flex items-center gap-1.5 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-mq-primary shadow-lg backdrop-blur transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mq-primary/40 ${
                  isRTL ? 'left-3' : 'right-3'
                }`}
              >
                <i className="ri-fullscreen-line" aria-hidden="true" />
                {t('home.v2.brokers.viewMap')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
